import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Home, Rss, Plus, Heart, User, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { SubmitCafeDialog } from "@/components/SubmitCafeDialog";
import { useOnboardingCompleteOnDevice } from "@/lib/onboardingFlag";

type Tab =
  | { type: "link"; href: string; label: string; icon: LucideIcon; key: string }
  | { type: "action"; label: string; icon: LucideIcon; key: string };

const tabs: Tab[] = [
  { type: "link", href: "/browse", label: "Home", icon: Home, key: "home" },
  { type: "link", href: "/feed", label: "Feed", icon: Rss, key: "feed" },
  { type: "action", label: "Suggest", icon: Plus, key: "suggest" },
  { type: "link", href: "/favorites", label: "Favorites", icon: Heart, key: "favorites" },
  { type: "link", href: "/profile", label: "Profile", icon: User, key: "profile" },
];

/** The owner area has its own menu; the regular app menu never shows there. */
export function isOwnerAreaPath(pathname: string): boolean {
  return pathname === "/owner" || pathname.startsWith("/owner/") || pathname === "/owner-login";
}

export function MobileBottomNav() {
  const location = useLocation();
  const { user } = useAuth();
  const [suggestOpen, setSuggestOpen] = useState(false);
  // Live: the menu appears the moment the device flag is set, no reload.
  const onboardingComplete = useOnboardingCompleteOnDevice();

  if (!user || !onboardingComplete) return null;
  if (isOwnerAreaPath(location.pathname)) return null;

  const isActive = (tab: Tab) => {
    if (tab.type === "action") return false;
    if (tab.key === "home") {
      return location.pathname === "/browse" || location.pathname === "/";
    }
    return location.pathname === tab.href || location.pathname.startsWith(`${tab.href}/`);
  };

  return (
    <>
      <nav
        aria-label="Primary"
        className="lg:hidden fixed bottom-0 inset-x-0 z-50 border-t border-border/30 glass-frost"
        // Guarantee breathing room above the home indicator. On devices with no
        // safe-area inset (older iPhones, Android) env() resolves to 0, which left
        // the tap targets flush against the screen edge — max() enforces a floor.
        style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0.75rem)" }}
      >
        {/* Opaque safe-area fill so iOS home indicator area matches the bar */}
        <div
          className="absolute inset-x-0 bottom-0 bg-background"
          style={{ height: "max(env(safe-area-inset-bottom), 0.75rem)" }}
        />
        <ul className="relative flex h-14 items-stretch">
          {tabs.map((tab) => {
            const { label, icon: Icon, key } = tab;
            const active = isActive(tab);
            const inner = (
              <>
                <span className={cn(
                  "flex items-center justify-center rounded-full transition-all",
                  active ? "bg-primary/10 px-4 py-1" : "px-4 py-1"
                )}>
                  <Icon
                    className="h-5 w-5"
                    strokeWidth={active ? 2.5 : 1.8}
                  />
                </span>
                <span className={cn(
                  "text-[10px] leading-none transition-all",
                  active ? "font-semibold" : "font-medium"
                )}>
                  {label}
                </span>
              </>
            );
            const className = cn(
              "flex h-full w-full min-h-[44px] flex-col items-center justify-center gap-1 px-2 transition-colors",
              active
                ? "text-primary"
                : "text-muted-foreground hover:text-foreground active:scale-95"
            );
            return (
              <li key={key} className="flex-1">
                {tab.type === "link" ? (
                  <Link
                    to={tab.href}
                    aria-current={active ? "page" : undefined}
                    className={className}
                  >
                    {inner}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => setSuggestOpen(true)}
                    className={className}
                  >
                    {inner}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </nav>
      <SubmitCafeDialog open={suggestOpen} onOpenChange={setSuggestOpen} entryPoint="mobile_bottom_nav" />
    </>
  );
}
